import React, { useEffect, useMemo, useState } from 'react';
import { Search, ExternalLink, UserCog, History, AlertTriangle, ShieldAlert, UserX, Landmark } from 'lucide-react';
import { STATUS_NEGOCIACAO_META } from '../../../../src/components/PainelNegociacaoCobranca';
import { useCpfsDosClientes, useEsteiraCobranca } from '../hooks/useEsteiraCobranca';
import { ClienteConsolidado } from '../types';
import type { RecorteDaTela } from '../lib/perfisExportacao';
import { formatCurrency } from '../utils';

/**
 * Corte de "débito antigo". 180 dias é o que o chamado pede; a régua de
 * cobrança automática termina em D+24, então tudo que aparece aqui saiu da
 * esteira normal há muito tempo.
 */
const DIAS_LIMITE = 180;

type FaixaAtraso = 'todas' | '180-364' | '365+';
type FiltroRateio = 'todos' | 'finalizado' | 'sem_rateio' | 'ativo' | 'nao_informada';
type FiltroEsteira = 'todos' | 'sem_registro' | 'sem_responsavel' | 'negativacao';

interface Props {
  clientes: ClienteConsolidado[];
  onNavigate?: (tab: string, clientName?: string) => void;
  onAbrirGerenciamento?: (cliente: ClienteConsolidado) => void;
  /** Reporta ao cabeçalho o que esta aba está mostrando, para o Excel sair igual. */
  onRecorteChange?: (recorte: RecorteDaTela) => void;
}

const DebitosAntigos: React.FC<Props> = ({ clientes, onNavigate, onAbrirGerenciamento, onRecorteChange }) => {
  const [busca, setBusca] = useState('');
  const [faixa, setFaixa] = useState<FaixaAtraso>('todas');
  const [rateio, setRateio] = useState<FiltroRateio>('todos');
  const [filtroEsteira, setFiltroEsteira] = useState<FiltroEsteira>('todos');

  // O recorte de +180 dias é sobre a fatura mais antiga em aberto do cliente.
  const antigos = useMemo(
    () => clientes
      // Conta interna (Associação Vigor, Sicoob...) não é associado devedor (#6166).
      .filter(c => c.diasDividaMax >= DIAS_LIMITE && !c.interno)
      .sort((a, b) => b.valorTotal - a.valorTotal),
    [clientes]
  );

  const cpfs = useCpfsDosClientes(antigos);
  const { esteira, carregando } = useEsteiraCobranca(cpfs);

  const visiveis = useMemo(() => antigos.filter(c => {
    if (faixa === '180-364' && c.diasDividaMax >= 365) return false;
    if (faixa === '365+' && c.diasDividaMax < 365) return false;
    if (rateio === 'finalizado' && c.situacaoRateio !== 'encerrado') return false;
    if (rateio === 'sem_rateio' && c.situacaoRateio !== 'sem_rateio') return false;
    if (rateio === 'ativo' && c.situacaoRateio !== 'ativo') return false;
    if (rateio === 'nao_informada' && c.situacaoRateio !== 'nao_informada') return false;

    const reg = c.cpfCnpj ? esteira.get(c.cpfCnpj) : undefined;
    if (filtroEsteira === 'sem_registro' && reg) return false;
    if (filtroEsteira === 'sem_responsavel' && reg?.responsavel_cobranca_id) return false;
    if (filtroEsteira === 'negativacao'
      && reg?.status_negociacao !== 'negativacao_pendente'
      && reg?.status_negociacao !== 'negativacao_realizada') return false;

    if (busca) {
      const b = busca.toLowerCase();
      const soDigitos = busca.replace(/\D/g, '');
      if (!c.nome.toLowerCase().includes(b)
        && !c.ucs.some(u => u.includes(busca))
        && !(soDigitos && (c.cpfCnpj || '').includes(soDigitos))) return false;
    }
    return true;
  }), [antigos, faixa, rateio, filtroEsteira, busca, esteira]);

  // Os filtros desta aba (faixa, rateio, esteira, busca) vivem aqui dentro; o
  // botão de exportar está no cabeçalho. Sem reportar, filtrar "365 dias ou
  // mais" e exportar devolvia os 180+ inteiros.
  const recorte: RecorteDaTela = useMemo(() => {
    const partes: string[] = [];
    if (faixa === '180-364') partes.push('de 180 a 364 dias');
    if (faixa === '365+') partes.push('365 dias ou mais');
    if (rateio === 'finalizado') partes.push('só rateio encerrado');
    if (rateio === 'ativo') partes.push('só rateio ativo');
    if (rateio === 'sem_rateio') partes.push('só sem rateio');
    if (rateio === 'nao_informada') partes.push('só situação não informada');
    if (filtroEsteira === 'sem_registro') partes.push('sem registro na esteira');
    if (filtroEsteira === 'sem_responsavel') partes.push('sem responsável pela cobrança');
    if (filtroEsteira === 'negativacao') partes.push('em negativação');
    if (busca.trim()) partes.push(`busca "${busca.trim()}"`);
    return {
      ids: visiveis.map(c => c.nome),
      descricao: partes.join(' · '),
      totalSemFiltro: antigos.length,
    };
  }, [visiveis, antigos.length, faixa, rateio, filtroEsteira, busca]);

  useEffect(() => { onRecorteChange?.(recorte); }, [recorte, onRecorteChange]);

  const resumo = useMemo(() => {
    const total = visiveis.reduce((s, c) => s + c.valorTotal, 0);
    const deRateioEncerrado = visiveis.filter(c => c.rateioFinalizado).reduce((s, c) => s + c.valorTotal, 0);
    const semDono = visiveis.filter(c => !c.cpfCnpj || !esteira.get(c.cpfCnpj)?.responsavel_cobranca_id).length;
    const emNegativacao = visiveis.filter(c => {
      const st = c.cpfCnpj ? esteira.get(c.cpfCnpj)?.status_negociacao : undefined;
      return st === 'negativacao_pendente' || st === 'negativacao_realizada';
    }).length;
    const faturas = visiveis.reduce((s, c) => s + c.qtdFaturas, 0);
    return { total, deRateioEncerrado, semDono, emNegativacao, faturas };
  }, [visiveis, esteira]);

  // Sem documento não há como abrir a esteira: cobranca_clientes é chaveada
  // por CPF/CNPJ. Avisar quem são, em vez de deixar o botão falhar calado.
  const semDocumento = useMemo(() => antigos.filter(c => !c.cpfCnpj), [antigos]);

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-4 flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <History size={20} className="text-red-500" />
            Débitos antigos ({DIAS_LIMITE}+ dias)
          </h2>
          <p className="text-sm text-gray-500 max-w-3xl">
            Associados fora da régua de cobrança — ela termina em D+24. Inclui quem já teve o rateio
            encerrado e seguiu devendo. A idade conta pela competência da fatura, não pela data do
            boleto: o que veio migrado do Power Rev foi reemitido com vencimento novo e parecia
            recente. O histórico de tentativas, negociações e negativação fica no
            botão <strong>Gerenciar</strong> de cada linha.
          </p>
        </div>
        <div className="relative w-64 flex-shrink-0">
          <input
            type="text"
            placeholder="Buscar cliente, UC ou CPF/CNPJ..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-[#5F6C37] focus:border-[#5F6C37]"
          />
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 flex-shrink-0">
        <ResumoCard
          icone={<AlertTriangle size={16} />}
          rotulo="Exposição total"
          valor={formatCurrency(resumo.total)}
          nota={`${visiveis.length} clientes · ${resumo.faturas} faturas`}
          tom="vermelho"
        />
        <ResumoCard
          icone={<Landmark size={16} />}
          rotulo="De rateio já encerrado"
          valor={formatCurrency(resumo.deRateioEncerrado)}
          nota="associado saiu, dívida ficou"
          tom="ambar"
        />
        <ResumoCard
          icone={<UserX size={16} />}
          rotulo="Sem responsável"
          valor={String(resumo.semDono)}
          nota="ninguém assumiu a cobrança"
          tom="cinza"
        />
        <ResumoCard
          icone={<ShieldAlert size={16} />}
          rotulo="Em negativação"
          valor={String(resumo.emNegativacao)}
          nota="pendente ou já executada"
          tom="vermelho"
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
        <GrupoFiltro rotulo="Atraso" valor={faixa} onChange={v => setFaixa(v as FaixaAtraso)} opcoes={[
          { v: 'todas', t: 'Todos' }, { v: '180-364', t: '180 a 364 dias' }, { v: '365+', t: '1 ano ou mais' },
        ]} />
        <GrupoFiltro rotulo="Rateio" valor={rateio} onChange={v => setRateio(v as FiltroRateio)} opcoes={[
          { v: 'todos', t: 'Todos' }, { v: 'finalizado', t: 'Encerrado' }, { v: 'sem_rateio', t: 'Sem rateio' },
          { v: 'ativo', t: 'Ativo' }, { v: 'nao_informada', t: 'Não informada' },
        ]} />
        <GrupoFiltro rotulo="Esteira" valor={filtroEsteira} onChange={v => setFiltroEsteira(v as FiltroEsteira)} opcoes={[
          { v: 'todos', t: 'Todos' }, { v: 'sem_registro', t: 'Sem registro' },
          { v: 'sem_responsavel', t: 'Sem responsável' }, { v: 'negativacao', t: 'Negativação' },
        ]} />
        {carregando ? <span className="text-[11px] text-gray-400">carregando esteira...</span> : null}
      </div>

      {semDocumento.length > 0 ? (
        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex-shrink-0">
          <strong>{semDocumento.length} cliente(s) sem CPF/CNPJ</strong> nem na fatura nem no cadastro da UC —
          para esses o gerenciamento de cobrança não abre. Cadastre o documento na Unidade Consumidora:{' '}
          {semDocumento.slice(0, 4).map(c => c.nome).join(', ')}
          {semDocumento.length > 4 ? ` e mais ${semDocumento.length - 4}` : ''}.
        </div>
      ) : null}

      <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-bold">Cliente</th>
                <th className="px-4 py-3 font-bold">UCs</th>
                <th className="px-4 py-3 font-bold text-right">Débito</th>
                <th className="px-4 py-3 font-bold text-center">Faturas</th>
                <th className="px-4 py-3 font-bold text-center">Idade da dívida</th>
                <th className="px-4 py-3 font-bold">Esteira de cobrança</th>
                <th className="px-4 py-3 font-bold text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    Nenhum débito com {DIAS_LIMITE}+ dias de atraso nos filtros atuais.
                  </td>
                </tr>
              ) : (
                visiveis.map(c => {
                  const reg = c.cpfCnpj ? esteira.get(c.cpfCnpj) : undefined;
                  const meta = reg ? STATUS_NEGOCIACAO_META[reg.status_negociacao] : null;
                  return (
                    <tr key={c.nome} className={`border-b border-gray-100 hover:bg-gray-50 ${c.interno ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-800">{c.nome}</div>
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          {c.interno ? <Etiqueta tom="cinza">INTERNO</Etiqueta> : null}
                          {c.situacaoRateio === 'encerrado' ? <Etiqueta tom="ambar">RATEIO ENCERRADO</Etiqueta> : null}
                          {c.situacaoRateio === 'sem_rateio' ? <Etiqueta tom="ambar">SEM RATEIO</Etiqueta> : null}
                          {c.situacaoRateio === 'nao_informada' ? <Etiqueta tom="cinza">SITUAÇÃO NÃO INFORMADA</Etiqueta> : null}
                          {!c.cpfCnpj ? <Etiqueta tom="vermelho">SEM CPF/CNPJ</Etiqueta> : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {c.ucs[0] || '—'}
                        {c.ucs.length > 1 ? <span className="text-gray-400"> +{c.ucs.length - 1}</span> : null}
                      </td>
                      <td className="px-4 py-3 text-right font-black text-[#5F6C37]">{formatCurrency(c.valorTotal)}</td>
                      <td className="px-4 py-3 text-center text-xs text-gray-600">{c.qtdFaturas}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.diasDividaMax >= 365 ? 'bg-red-200 text-red-900' : 'bg-red-100 text-red-700'}`}>
                          {c.diasDividaMax}d
                        </span>
                        {/* Boleto muito mais novo que a dívida = fatura migrada do
                            Power Rev, reemitida com vencimento novo. Sem isso a
                            linha parece um atraso pequeno. */}
                        {c.diasDividaMax - c.diasAtrasoMax >= 60 ? (
                          <div
                            className="text-[9px] text-amber-600 mt-0.5"
                            title={`A dívida é da competência ${c.competenciaMaisAntiga || '—'}, mas o boleto foi reemitido e venceu há ${c.diasAtrasoMax} dias.`}
                          >
                            boleto reemitido · {c.diasAtrasoMax}d
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        {meta ? (
                          <div className="flex flex-col gap-1">
                            <span className={`self-start px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.classes}`}>
                              {meta.label}
                            </span>
                            <span className="text-[10px] text-gray-500">
                              {reg?.responsavel_cobranca_id ? 'com responsável' : 'sem responsável'}
                              {reg?.proximo_contato_em
                                ? ` · retorno ${new Date(reg.proximo_contato_em + 'T00:00:00').toLocaleDateString('pt-BR')}`
                                : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">sem registro de cobrança</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            title="Detalhes (Perfil 360)"
                            onClick={() => onNavigate?.('perfil', c.nome)}
                            className="text-[#F6A937] hover:text-orange-500 transition-colors p-1 rounded hover:bg-orange-50"
                          >
                            <ExternalLink size={16} />
                          </button>
                          {onAbrirGerenciamento ? (
                            <button
                              title={c.cpfCnpj ? 'Gerenciar cobrança e histórico' : 'Sem CPF/CNPJ — cadastre na UC'}
                              disabled={!c.cpfCnpj}
                              onClick={() => onAbrirGerenciamento(c)}
                              className="text-[#5F6C37] hover:text-[#4a5429] transition-colors p-1 rounded hover:bg-green-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <UserCog size={16} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const TONS = {
  vermelho: 'bg-red-50 border-red-100 text-red-700',
  ambar: 'bg-amber-50 border-amber-100 text-amber-700',
  cinza: 'bg-gray-50 border-gray-200 text-gray-600',
} as const;

const ResumoCard: React.FC<{
  icone: React.ReactNode; rotulo: string; valor: string; nota: string; tom: keyof typeof TONS;
}> = ({ icone, rotulo, valor, nota, tom }) => (
  <div className={`p-3 rounded-xl border ${TONS[tom]}`}>
    <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide opacity-80">
      {icone}{rotulo}
    </div>
    <p className="text-lg font-black mt-1 text-gray-800">{valor}</p>
    <p className="text-[10px] text-gray-500">{nota}</p>
  </div>
);

const Etiqueta: React.FC<{ tom: keyof typeof TONS; children: React.ReactNode }> = ({ tom, children }) => (
  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${TONS[tom]}`}>{children}</span>
);

const GrupoFiltro: React.FC<{
  rotulo: string; valor: string; onChange: (v: string) => void; opcoes: { v: string; t: string }[];
}> = ({ rotulo, valor, onChange, opcoes }) => (
  <div className="flex items-center gap-1">
    <span className="text-[10px] font-black uppercase text-gray-400">{rotulo}</span>
    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
      {opcoes.map(o => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${valor === o.v ? 'bg-[#5F6C37] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        >
          {o.t}
        </button>
      ))}
    </div>
  </div>
);

export default DebitosAntigos;
